/**
 * Aviso de versión nueva y rescate cuando un chunk ya no existe.
 *
 * ─── El problema que resuelve ───────────────────────────────────────────────
 * El build parte la app en archivos con hash (`assets/algo-A1b2C3.js`) y cada
 * despliegue a Firebase Hosting **borra los del release anterior**. Una pestaña
 * abierta desde antes del despliegue sigue con el `index.html` viejo, así que
 * cuando pide un trozo que solo se carga bajo demanda —`await import(...)`, que
 * en este repo son 40 y pico: el diálogo de oferta, los PDF, los exportadores—
 * ese archivo ya no está.
 *
 * Y no responde 404: el `rewrite` de Hosting manda TODO lo que no existe a
 * `/index.html`, así que el navegador recibe HTML con status 200 donde esperaba
 * JavaScript y falla con "Failed to fetch dynamically imported module". Le pasó
 * a un asesor a media oferta comercial, sin ninguna pista de que lo único que
 * necesitaba era recargar.
 *
 * ─── Qué hace ───────────────────────────────────────────────────────────────
 * 1. Caza ese fallo (por el evento de Vite y por los errores sueltos) y lo
 *    convierte en un aviso que dice qué pasó y ofrece recargar.
 * 2. Pregunta cada tanto por `version.json` para avisar ANTES de que truene, que
 *    es la diferencia entre recargar con el formulario vacío o con él lleno.
 *
 * NO recarga sola. Quien está a media captura pierde lo escrito, y eso duele más
 * que el error: la decisión es suya.
 */

import { APP_VERSION } from "@/lib/config";

/** Cada cuánto se pregunta por la versión publicada. */
const INTERVALO_CHEQUEO_MS = 5 * 60 * 1000;

/** Una sola vez por pestaña: el aviso no se repite ni se apila. */
let avisoMostrado = false;

/**
 * ¿El error es "el chunk que pedí ya no existe"?
 *
 * Los navegadores redactan distinto el mismo fallo, y ninguno usa un código: hay
 * que mirar el texto. Se aceptan las variantes de Chrome, Firefox y Safari.
 */
function esErrorDeChunk(motivo: unknown): boolean {
  const texto = String(
    (motivo as { message?: string } | null)?.message ?? motivo ?? "",
  ).toLowerCase();
  return (
    texto.includes("dynamically imported module") ||
    texto.includes("error loading dynamically imported module") ||
    texto.includes("importing a module script failed") ||
    texto.includes("failed to fetch dynamically imported module")
  );
}

/** Deja el navegador sin service workers ni cachés y recarga. */
async function recargarLimpio(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const registros = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registros.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const nombres = await caches.keys();
      await Promise.all(nombres.map((n) => caches.delete(n)));
    }
  } catch {
    // Si el navegador no deja limpiar, la recarga sola ya sirve: el index nuevo
    // pide otros hashes y el que falló no se vuelve a tocar.
  }
  window.location.reload();
}

/**
 * Pinta el aviso en DOM plano, sin React.
 *
 * A propósito: cuando esto se dispara puede haber un trozo de la app que no
 * cargó, así que el aviso no puede depender de que el árbol de React siga sano.
 * Usa los tokens de color del tema para no desentonar en claro ni en oscuro.
 */
function mostrarAviso(mensaje: string, urgente: boolean): void {
  if (avisoMostrado) return;
  avisoMostrado = true;

  const barra = document.createElement("div");
  barra.setAttribute("role", "alert");
  barra.style.cssText = [
    "position:fixed",
    "left:50%",
    "bottom:24px",
    "transform:translateX(-50%)",
    "z-index:2147483647",
    "max-width:min(640px, calc(100vw - 32px))",
    "display:flex",
    "align-items:center",
    "gap:16px",
    "padding:14px 18px",
    "border-radius:12px",
    "border:1px solid hsl(var(--border))",
    "background:hsl(var(--card))",
    "color:hsl(var(--card-foreground))",
    "box-shadow:0 12px 32px rgb(0 0 0 / 0.18)",
    "font-family:inherit",
    "font-size:14px",
    "line-height:1.45",
  ].join(";");

  const texto = document.createElement("div");
  texto.style.cssText = "flex:1;min-width:0";
  texto.textContent = mensaje;

  const boton = document.createElement("button");
  boton.type = "button";
  boton.textContent = "Recargar";
  boton.style.cssText = [
    "flex:none",
    "padding:8px 16px",
    "border-radius:8px",
    "border:0",
    "cursor:pointer",
    "font-weight:600",
    "font-size:14px",
    "background:hsl(var(--primary))",
    "color:hsl(var(--primary-foreground))",
  ].join(";");
  boton.onclick = () => {
    boton.disabled = true;
    boton.textContent = "Recargando...";
    void recargarLimpio();
  };

  barra.append(texto, boton);

  // El aviso de "hay versión nueva" se puede posponer; el de "esto ya no
  // funciona" no, porque cerrarlo deja al usuario dándole a un botón muerto.
  if (!urgente) {
    const despues = document.createElement("button");
    despues.type = "button";
    despues.textContent = "Después";
    despues.style.cssText = [
      "flex:none",
      "padding:8px 12px",
      "border-radius:8px",
      "border:1px solid hsl(var(--border))",
      "cursor:pointer",
      "font-size:14px",
      "background:transparent",
      "color:hsl(var(--muted-foreground))",
    ].join(";");
    despues.onclick = () => {
      barra.remove();
      // Se permite que vuelva a avisar más tarde: si sigue con la pestaña
      // abierta, el siguiente chequeo lo recuerda.
      avisoMostrado = false;
    };
    barra.append(despues);
  }

  document.body.appendChild(barra);
}

const MENSAJE_CHUNK = import.meta.env.DEV
  ? "El servidor local recompiló dependencias y esta pestaña quedó con las anteriores. " +
    "Recarga para continuar (lo que no hayas guardado se pierde)."
  : "Se publicó una versión nueva de la plataforma y esta pestaña quedó con la anterior. " +
  "Recarga para continuar (lo que no hayas guardado se pierde).";

const MENSAJE_VERSION =
  "Hay una versión nueva de la plataforma. Recarga cuando termines lo que estás haciendo.";

/** ¿La versión publicada es otra que la de esta pestaña? */
async function hayVersionNueva(): Promise<boolean> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { version?: string };
    return !!data.version && data.version !== APP_VERSION;
  } catch {
    // Sin red no se sabe, y no se avisa: un falso positivo aquí manda a recargar
    // a quien no lo necesita.
    return false;
  }
}

/**
 * Arranca la vigilancia. Se llama una vez, desde `main.tsx`.
 */
export function instalarAvisoDeActualizacion(): void {
  if (typeof window === "undefined") return;

  // Vite avisa por su cuenta cuando no puede precargar un chunk. NO se llama
  // `preventDefault`: en Vite 5 eso hace que el helper de precarga se trague el
  // error y el `import()` RESUELVA con `undefined` en vez de rechazar. Entonces
  // `React.lazy` truena con "undefined is not an object (evaluating
  // 't._result.default')" (SOZU-ADMIN-1, ticket #1439) y el `lazyRetry` de
  // App.tsx nunca ve el fallo para recargar. Dejar que rechace es lo correcto.
  window.addEventListener("vite:preloadError", () => {
    mostrarAviso(MENSAJE_CHUNK, true);
  });

  // Red de seguridad para el `import()` que falla fuera del preload de Vite:
  // según el navegador llega como error global o como promesa rechazada.
  window.addEventListener("error", (evento) => {
    if (esErrorDeChunk(evento.error ?? evento.message)) mostrarAviso(MENSAJE_CHUNK, true);
  });
  window.addEventListener("unhandledrejection", (evento) => {
    if (esErrorDeChunk(evento.reason)) mostrarAviso(MENSAJE_CHUNK, true);
  });

  const revisar = () => {
    void hayVersionNueva().then((nueva) => {
      if (nueva) mostrarAviso(MENSAJE_VERSION, false);
    });
  };

  window.setInterval(revisar, INTERVALO_CHEQUEO_MS);
  // Volver a la pestaña es el momento más probable de haberse quedado atrás:
  // el despliegue suele pasar mientras estaba en otra cosa.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") revisar();
  });
}
